import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Crown, 
  Zap, 
  Package, 
  Calendar, 
  CreditCard, 
  ArrowRight,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  Users,
  Target
} from 'lucide-react';
import api from '../services/api';
import { getPlanDisplayName, PLAN_FEATURES } from '../utils/subscriptionUtils';

export const SubscriptionManagement = ({ userEmail, onSubscriptionChange }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userEmail) {
      loadSubscriptionData();
    }
  }, [userEmail]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUserSubscriptionStatus(userEmail);
      setSubscriptionData(data);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    try {
      setCancelling(true);
      await api.cancelUserSubscription(userEmail, subscriptionData.paypal_subscription_id);
      
      // Reload subscription data
      await loadSubscriptionData();
      
      if (onSubscriptionChange) {
        onSubscriptionChange('free');
      }
      
      alert('Subscription cancelled successfully. You will retain premium access until the end of your current billing period.');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2">Loading subscription details...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <Button 
            onClick={loadSubscriptionData} 
            variant="outline" 
            size="sm" 
            className="mt-3"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isPremium = subscriptionData?.plan !== 'free';
  const isActive = subscriptionData?.status === 'active';
  const isCancelled = subscriptionData?.status === 'cancelled';

  return (
    <div className="space-y-6">
      {/* Current Plan Overview */}
      <Card className={`${isPremium ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isPremium ? (
              <Crown className="w-5 h-5 text-emerald-600" />
            ) : (
              <Package className="w-5 h-5 text-gray-600" />
            )}
            Current Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-gray-900">
                  {getPlanDisplayName(subscriptionData.plan)}
                </h3>
                <Badge 
                  className={`${
                    isPremium 
                      ? 'bg-emerald-600 hover:bg-emerald-700' 
                      : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {subscriptionData.plan.toUpperCase()}
                </Badge>
                {isCancelled && (
                  <Badge variant="destructive">
                    CANCELLED
                  </Badge>
                )}
              </div>
              <p className="text-gray-600">
                {isPremium 
                  ? 'Unlimited tracking with premium features'
                  : 'Basic tracking with limited features'
                }
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {isPremium ? '$1.99' : '$0'}
              </div>
              <div className="text-sm text-gray-500">
                {isPremium ? 'per month' : 'forever'}
              </div>
            </div>
          </div>

          {/* Item Usage */}
          <div className="bg-white rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700">Items Created</span>
              <span className="text-sm text-gray-500">
                {subscriptionData.item_count} / {subscriptionData.max_items === -1 ? '∞' : subscriptionData.max_items}
              </span>
            </div>
            
            {subscriptionData.max_items !== -1 && (
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    subscriptionData.item_count >= subscriptionData.max_items
                      ? 'bg-red-500'
                      : subscriptionData.item_count > subscriptionData.max_items * 0.8
                      ? 'bg-yellow-500'
                      : 'bg-emerald-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (subscriptionData.item_count / subscriptionData.max_items) * 100)}%` 
                  }}
                ></div>
              </div>
            )}
            
            {!isPremium && subscriptionData.remaining_items <= 5 && (
              <p className="text-sm text-orange-600 mt-2">
                ⚠️ Only {subscriptionData.remaining_items} items remaining
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            {!isPremium ? (
              <Button 
                onClick={handleUpgrade}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to Premium
              </Button>
            ) : (
              <>
                {isActive && (
                  <Button 
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    variant="outline"
                    className="flex-1"
                  >
                    {cancelling ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    Cancel Subscription
                  </Button>
                )}
                <Button 
                  onClick={() => window.open(subscriptionData.paypal_subscription_id ? `https://www.paypal.com/myaccount/autopay/` : '#', '_blank')}
                  variant="outline"
                  className="flex-1"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Feature Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Feature Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <div className={`p-4 rounded-lg border-2 ${!isPremium ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-gray-600" />
                <h4 className="font-semibold">Free Explorer</h4>
                {!isPremium && <Badge variant="default">CURRENT</Badge>}
              </div>
              <ul className="space-y-2 text-sm">
                {PLAN_FEATURES.free.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium Plan */}
            <div className={`p-4 rounded-lg border-2 ${isPremium ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-emerald-600" />
                <h4 className="font-semibold">Premium Tracker</h4>
                {isPremium && <Badge className="bg-emerald-600">CURRENT</Badge>}
              </div>
              <ul className="space-y-2 text-sm max-h-48 overflow-y-auto">
                {PLAN_FEATURES.premium.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Information */}
      {isPremium && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Billing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Status</label>
                <div className="flex items-center gap-2 mt-1">
                  {isActive ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-700">Active</span>
                    </>
                  ) : isCancelled ? (
                    <>
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-red-700">Cancelled</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-yellow-500" />
                      <span className="text-yellow-700">{subscriptionData.status}</span>
                    </>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Next Billing</label>
                <p className="text-gray-900 mt-1">
                  {subscriptionData.next_billing_date 
                    ? new Date(subscriptionData.next_billing_date).toLocaleDateString()
                    : 'Not available'
                  }
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Subscription Started</label>
                <p className="text-gray-900 mt-1">
                  {subscriptionData.subscription_created 
                    ? new Date(subscriptionData.subscription_created).toLocaleDateString()
                    : 'Not available'
                  }
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Payment Method</label>
                <div className="flex items-center gap-2 mt-1">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-900">PayPal</span>
                </div>
              </div>
            </div>

            {isCancelled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Subscription Cancelled</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Your premium features will remain active until your next billing date. 
                      After that, your account will be downgraded to the free plan.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionManagement;